import { useState } from "react";
import { Eye, EyeOff, Scissors, ArrowRight, Lock, User } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import T from "../theme/colors";

export default function Login({ onLogin }) {
  const { login, error, setError } = useAuth();
  const [username, setLoginId]   = useState("");
  const [password, setPassword] = useState("");
  const [showPwd, setShowPwd]   = useState(false);
  const [loading, setLoading]   = useState(false);
  const [fieldErrors, setFieldErrors] = useState({});

  const validate = () => {
    const errs = {};
    if (!username.trim())  errs.username  = "Username is required";
    if (!password.trim()) errs.password = "Password is required";
    setFieldErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    setLoading(true);
    setError(null);
    try {
      await login(username, password);
      // Clear any stale hash left over from the user's last session (e.g. if
      // they logged out from #invoice-register-l2 they shouldn't come back in
      // trying to navigate there before permissions rehydrate).
      // AppLayout's resolveInitialPage() will pick dashboard if allowed,
      // or the user's first accessible page otherwise.
      if (window.location.hash) {
        window.history.replaceState(null, "", window.location.pathname + window.location.search);
      }
      onLogin?.();
    } catch (err) {
      // error is set in AuthContext
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{
      minHeight: "100vh", background: "var(--bg-app)",
      display: "flex", fontFamily: "var(--font-ui)",
    }}>

      {/* Left panel — branding */}
      <div style={{
        width: 420, flexShrink: 0,
        background: "#1A1612",
        display: "flex", flexDirection: "column",
        padding: "48px 44px",
        position: "relative", overflow: "hidden",
      }}>
        {/* Decorative pattern */}
        <div style={{
          position: "absolute", inset: 0, pointerEvents: "none",
          backgroundImage: `radial-gradient(circle at 30% 20%, rgba(201,82,16,0.18) 0%, transparent 60%),
                            radial-gradient(circle at 70% 80%, rgba(196,16,64,0.14) 0%, transparent 60%)`,
        }} />
        <div style={{
          position: "absolute", bottom: -80, right: -80,
          width: 300, height: 300, borderRadius: "50%",
          border: "1px solid rgba(255,255,255,0.04)",
          pointerEvents: "none",
        }} />
        <div style={{
          position: "absolute", bottom: -20, right: -20,
          width: 200, height: 200, borderRadius: "50%",
          border: "1px solid rgba(255,255,255,0.04)",
          pointerEvents: "none",
        }} />

        {/* Logo */}
        <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: "auto" }}>
          <div style={{
            width: 38, height: 38, borderRadius: 10, background: "var(--grad)",
            display: "flex", alignItems: "center", justifyContent: "center",
            boxShadow: "0 4px 16px rgba(201,82,16,0.4)",
          }}>
            <Scissors size={18} style={{ color: "#fff" }} />
          </div>
          <div>
            <div style={{ fontSize: 18, fontWeight: 600, color: "#fff", fontFamily: "var(--font-display)" }}>
              GarmentERP
            </div>
            <div style={{ fontSize: 13, color: "rgba(255,255,255,0.4)", marginTop: 1 }}>
              SM IT Solutions
            </div>
          </div>
        </div>

        {/* Main copy */}
        <div style={{ position: "relative" }}>
          <h2 style={{
            fontSize: 32, fontWeight: 600, color: "#fff",
            fontFamily: "var(--font-display)", lineHeight: 1.3,
            marginBottom: 16,
          }}>
            Uniform &amp; Garment<br />
            <span style={{ color: "var(--primary)" }}>Management</span><br />
            System
          </h2>
          <p style={{ fontSize: 15, color: "rgba(255,255,255,0.5)", lineHeight: 1.7, marginBottom: 32 }}>
            End-to-end ERP for procurement, production and delivery of uniforms for government and enterprise clients across Maharashtra.
          </p>

          {/* Feature list */}
          {[
            "Vendor & Customer Management",
            "Purchase Orders & GRN with Bale Tracking",
            "Pattern & Product Master",
            "Sale Order with Production Workflow",
          ].map((f, i) => (
            <div key={i} style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 10 }}>
              <div style={{
                width: 18, height: 18, borderRadius: "50%",
                background: "rgba(201,82,16,0.2)",
                border: "1px solid rgba(201,82,16,0.4)",
                display: "flex", alignItems: "center", justifyContent: "center",
                flexShrink: 0,
              }}>
                <span style={{ width: 5, height: 5, borderRadius: "50%", background: "var(--primary)", display: "block" }} />
              </div>
              <span style={{ fontSize: 14, color: "rgba(255,255,255,0.55)" }}>{f}</span>
            </div>
          ))}
        </div>

        {/* Footer */}
        <div style={{ marginTop: 48, paddingTop: 20, borderTop: "1px solid rgba(255,255,255,0.07)" }}>
          <p style={{ fontSize: 13, color: "rgba(255,255,255,0.25)" }}>
            © {new Date().getFullYear()} SM IT Solutions · Solapur, Maharashtra
          </p>
        </div>
      </div>

      {/* Right panel — form */}
      <div style={{
        flex: 1, display: "flex", alignItems: "center", justifyContent: "center",
        padding: 32,
      }}>
        <div style={{ width: "100%", maxWidth: 400 }} className="fade-up">
          {/* Header */}
          <div style={{ marginBottom: 36 }}>
            <h1 style={{
              fontSize: 25, fontWeight: 600, color: "var(--text-0)",
              fontFamily: "var(--font-display)", marginBottom: 6,
            }}>
              Welcome back
            </h1>
            <p style={{ fontSize: 15, color: "var(--text-1)" }}>
              Sign in to your account to continue
            </p>
          </div>

          {/* Global error */}
          {error && (
            <div style={{
              background: "var(--danger-bg)", border: `1px solid var(--danger-bdr)`,
              borderRadius: 7, padding: "10px 14px", marginBottom: 20,
              fontSize: 15, color: "var(--danger)", display: "flex", alignItems: "center", gap: 8,
            }}>
              <Lock size={13} style={{ flexShrink: 0 }} />
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} style={{ display: "flex", flexDirection: "column", gap: 16 }}>
            {/* Username */}
            <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
              <label style={{ fontSize: 14, fontWeight: 500, color: "var(--text-1)" }}>
                Username <span style={{ color: "var(--primary)" }}>*</span>
              </label>
              <div style={{
                display: "flex", alignItems: "center", gap: 8,
                background: "var(--bg-input)", border: `1px solid ${fieldErrors.username ? "var(--danger)" : "var(--border)"}`,
                borderRadius: 7, padding: "0 12px",
                boxShadow: fieldErrors.username ? `0 0 0 3px var(--danger-bg)` : "none",
                transition: "border-color 0.15s, box-shadow 0.15s",
              }}>
                <User size={14} style={{ color: "var(--text-2)", flexShrink: 0 }} />
                <input
                  type="text" value={username} onChange={e => { setLoginId(e.target.value); setFieldErrors(p => ({ ...p, username: "" })); }}
                  placeholder="e.g. admin or you@company.com" autoComplete="username" autoFocus
                  style={{ background: "none", border: "none", padding: "10px 0", flex: 1, fontSize: 15, color: "var(--text-0)", fontFamily: "var(--font-ui)" }}
                />
              </div>
              {fieldErrors.username && <span style={{ fontSize: 13, color: "var(--danger)" }}>⚠ {fieldErrors.username}</span>}
            </div>

            {/* Password */}
            <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <label style={{ fontSize: 14, fontWeight: 500, color: "var(--text-1)" }}>
                  Password <span style={{ color: "var(--primary)" }}>*</span>
                </label>
                <button type="button" style={{ background: "none", border: "none", fontSize: 13, color: "var(--primary)", cursor: "pointer", fontFamily: "var(--font-ui)" }}>
                  Forgot password?
                </button>
              </div>
              <div style={{
                display: "flex", alignItems: "center", gap: 8,
                background: "var(--bg-input)", border: `1px solid ${fieldErrors.password ? "var(--danger)" : "var(--border)"}`,
                borderRadius: 7, padding: "0 12px",
                boxShadow: fieldErrors.password ? `0 0 0 3px var(--danger-bg)` : "none",
                transition: "border-color 0.15s, box-shadow 0.15s",
              }}>
                <Lock size={14} style={{ color: "var(--text-2)", flexShrink: 0 }} />
                <input
                  type={showPwd ? "text" : "password"} value={password}
                  onChange={e => { setPassword(e.target.value); setFieldErrors(p => ({ ...p, password: "" })); }}
                  placeholder="Enter your password" autoComplete="current-password"
                  style={{ background: "none", border: "none", padding: "10px 0", flex: 1, fontSize: 15, color: "var(--text-0)", fontFamily: "var(--font-ui)" }}
                />
                <button
                  type="button" onClick={() => setShowPwd(s => !s)}
                  style={{ background: "none", border: "none", color: "var(--text-2)", cursor: "pointer", display: "flex", padding: 2 }}
                >
                  {showPwd ? <EyeOff size={14} /> : <Eye size={14} />}
                </button>
              </div>
              {fieldErrors.password && <span style={{ fontSize: 13, color: "var(--danger)" }}>⚠ {fieldErrors.password}</span>}
            </div>

            {/* Submit */}
            <button
              type="submit"
              disabled={loading}
              style={{
                marginTop: 8, width: "100%", padding: "11px 20px",
                background: loading ? "var(--primary-dim)" : "var(--grad)",
                border: "none", borderRadius: 7, cursor: loading ? "wait" : "pointer",
                color: loading ? "var(--primary)" : "#fff",
                fontSize: 16, fontWeight: 600, fontFamily: "var(--font-ui)",
                display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
                transition: "all 0.17s",
                boxShadow: loading ? "none" : "0 2px 8px rgba(201,82,16,0.3)",
              }}
              onMouseEnter={e => { if (!loading) e.currentTarget.style.boxShadow = "0 4px 16px rgba(201,82,16,0.4)"; }}
              onMouseLeave={e => { if (!loading) e.currentTarget.style.boxShadow = "0 2px 8px rgba(201,82,16,0.3)"; }}
            >
              {loading
                ? <><span style={{ width: 14, height: 14, border: `2px solid var(--primary)`, borderTopColor: "transparent", borderRadius: "50%", animation: "spin 0.7s linear infinite", display: "inline-block" }} /> Signing in…</>
                : <><span>Sign In</span><ArrowRight size={15} /></>
              }
            </button>
          </form>

          {/* Demo hint */}
          <div style={{
            marginTop: 28, padding: "12px 14px",
            background: "var(--info-bg)", border: `1px solid var(--info-bdr)`,
            borderRadius: 7,
          }}>
            <p style={{ fontSize: 13, color: "var(--info)", lineHeight: 1.6 }}>
              <strong>Demo credentials:</strong><br />
              Username: <code style={{ fontSize: 13 }}>admin</code>&nbsp; Password: <code style={{ fontSize: 13 }}>Admin@123</code>
            </p>
          </div>

          <p style={{ fontSize: 13, color: "var(--text-2)", textAlign: "center", marginTop: 24 }}>
            Powered by <strong style={{ color: "var(--text-1)" }}>SM IT Solutions</strong> · Solapur
          </p>
        </div>
      </div>
    </div>
  );
}
